<script lang="ts">
  /**
   * Read-only map-preview för en runda (S-04-25).
   *
   * Renderar rundans MULTILINESTRING-geometri på en MapLibre-instans och
   * fitBounds:ar till geometrins bbox. Återanvänder samma Protomaps
   * vector-tile-config som MapView, men utan layer-toggle eller
   * interaktiva kontroller utöver pan/zoom.
   *
   * Hard-rules:
   *  - Inga state i localStorage/sessionStorage. Reload = clean state.
   *  - Inget edit-stöd — komponenten är pure display.
   *  - Ingen ny datakategori; geometrin kommer redan från admin-listan.
   */
  import { onDestroy } from 'svelte';
  import maplibregl, { type Map as MapLibreMap, type StyleSpecification } from 'maplibre-gl';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import { readProtomapsApiKey } from '../../config/map';
  import { vectorDarkStyle } from '../../config/mapStyles';
  import { parseMultiLineStringWkt } from '../util/wkt';
  import { renderColorFor } from '../util/colorPalette';
  import type { HearthPriority } from './hearthsClient';

  interface Props {
    geometryWkt: string;
    priority: HearthPriority;
    /**
     * Optional kortnamn från palett-listan (S-04-24). Null/undefined =
     * priority-baserad fallback (samma som tidigare RouteMapPreview-render).
     */
    displayColor?: string | null;
  }

  const { geometryWkt, priority, displayColor = null }: Props = $props();
  const apiKey: string | null = $derived(readProtomapsApiKey());

  const polylines: Array<Array<[number, number]>> = $derived(parseMultiLineStringWkt(geometryWkt));

  let mapContainer: HTMLDivElement | undefined = $state();
  let mapInstance: MapLibreMap | null = null;

  const SOURCE_ID = 'route-preview';
  const LAYER_ID = 'route-preview-line';

  function priorityLineWidth(p: HearthPriority): number {
    if (p === 'high') return 5;
    if (p === 'medium') return 4;
    return 3;
  }

  // Färglogik delas via renderColorFor i colorPalette.ts (S-04-24b).
  // Vi använder priority som fallback om displayColor är null.

  function buildGeoJson(): GeoJSON.FeatureCollection<GeoJSON.MultiLineString> {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'MultiLineString',
            coordinates: polylines as unknown as Array<Array<[number, number]>>,
          },
        },
      ],
    };
  }

  function computeBbox(): [[number, number], [number, number]] | null {
    if (polylines.length === 0) return null;
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const line of polylines) {
      for (const [lng, lat] of line) {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      }
    }
    if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  }

  $effect(() => {
    if (!mapContainer) return;
    if (!apiKey) return;

    const style: StyleSpecification | null = vectorDarkStyle(apiKey);
    if (!style) return;

    const bbox = computeBbox();
    const center: [number, number] = bbox
      ? [(bbox[0][0] + bbox[1][0]) / 2, (bbox[0][1] + bbox[1][1]) / 2]
      : [11.95, 57.74];
    const map = new maplibregl.Map({
      container: mapContainer,
      style,
      center,
      zoom: 14,
      attributionControl: { compact: true },
    });

    mapInstance = map;

    map.once('load', () => {
      map.addSource(SOURCE_ID, { type: 'geojson', data: buildGeoJson() });
      map.addLayer({
        id: LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': renderColorFor(displayColor, priority),
          'line-width': priorityLineWidth(priority),
          'line-opacity': 0.9,
        },
      });

      const bb = computeBbox();
      if (bb) {
        map.fitBounds(bb, { padding: 32, duration: 0, maxZoom: 17 });
      }
    });

    return () => {
      map.remove();
      mapInstance = null;
    };
  });

  onDestroy(() => {
    mapInstance?.remove();
    mapInstance = null;
  });
</script>

{#if !apiKey}
  <div class="preview-fallback" role="status">
    Kart-förhandsvisning otillgänglig — Protomaps-nyckel saknas.
  </div>
{:else}
  <div class="preview-container" bind:this={mapContainer} aria-label="Rundans geometri"></div>
{/if}

<style>
  .preview-container {
    width: 100%;
    height: 320px;
    border-radius: 4px;
    border: 1px solid var(--rule);
    overflow: hidden;
  }
  .preview-fallback {
    padding: 1rem;
    border: 1px dashed var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    text-align: center;
    font-size: 0.9rem;
  }
</style>
